import React from 'react';
import { Apple, Flame, CheckCircle2, TrendingUp, AlertTriangle, ShieldCheck, HeartPulse, ZoomIn } from 'lucide-react';
import FoodLogPhoto from './FoodLogPhoto';

export const LOG_CARD_CLASS = 'w-full max-w-[420px] rounded-lg border backdrop-blur-lg bg-white/70 dark:bg-white/10 border-white/50 dark:border-white/10 shadow-[0_8px_24px_rgba(28,28,30,0.08)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.28)] overflow-hidden';

function getVerdict(score) {
  if (score == null) return null;
  const num = Number(score);
  if (num >= 9) return 'strong';
  if (num >= 7) return 'on';
  if (num >= 4) return 'mixed';
  return 'off';
}

function VerdictPill({ score, type, language }) {
  if (score == null) return null;
  const he = language === 'hebrew';
  const verdict = getVerdict(score);

  let label = '';
  let colorClass = '';
  let Icon = null;

  if (verdict === 'strong') {
    label = he ? 'מצוין' : 'Strong';
    colorClass = 'bg-[color:var(--progress)]/10 text-[color:var(--progress)]';
    Icon = type === 'health' ? HeartPulse : ShieldCheck;
  } else if (verdict === 'on') {
    label = he ? 'בתוכנית' : 'On Plan';
    colorClass = 'bg-[color:var(--progress)]/10 text-[color:var(--progress)]';
    Icon = CheckCircle2;
  } else if (verdict === 'mixed') {
    label = he ? 'חלקי' : 'Mixed';
    colorClass = 'bg-[color:var(--warning)]/10 text-[color:var(--warning)]';
    Icon = TrendingUp;
  } else {
    label = he ? 'חריגה' : 'Off Plan';
    colorClass = 'bg-[color:var(--danger-bg)] text-[color:var(--danger-text)]';
    Icon = AlertTriangle;
  }

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${colorClass}`} title={`${score}/10`}>
      <Icon size={12} aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

function CardMeta({ label, timeLabel, calories, planScore, healthScore, language, he }) {
  return (
    <div className="flex-1 min-w-0 flex flex-col justify-start gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[14px] font-medium text-ink dark:text-ink-dark truncate">{label}</span>
        <span className="text-[11px] text-ink-muted dark:text-ink-muted-dark tabular-nums flex-shrink-0">{timeLabel}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {calories != null && (
          <span className="inline-flex items-center gap-1 text-[13px] font-medium text-ink dark:text-ink-dark tabular-nums bg-moss dark:bg-moss-dark px-2 py-0.5 rounded-full">
            <Flame size={12} className="text-ink-muted" aria-hidden="true" />
            {calories} <span className="text-[11px] font-normal text-ink-muted">{he ? 'קק״ל' : 'kcal'}</span>
          </span>
        )}
        <VerdictPill score={planScore} type="plan" language={language} />
        <VerdictPill score={healthScore} type="health" language={language} />
      </div>
    </div>
  );
}

export default function FoodLogCard({ event, language, timeLabel, onOpen, onImageClick, tourId }) {
  const log = event.payload;
  const label = log.meal_label
    || (language === 'hebrew' ? 'ארוחה' : 'Logged meal');
  const calories = log.total_calories != null ? Math.round(log.total_calories) : null;
  const planScore = log.plan_score != null ? log.plan_score : null;
  const healthScore = log.health_score != null ? log.health_score : null;
  const he = language === 'hebrew';
  const hasPhoto = Boolean(log.has_image || log.image_url);

  const openDrawer = () => onOpen(log.id);

  return (
    <div className="flex w-full justify-center">
      <article className={LOG_CARD_CLASS}>
        {hasPhoto && (
          <div className="relative group">
            <FoodLogPhoto
              log={log}
              alt={label}
              language={language}
              className="w-full max-h-64 object-cover"
              onClick={(src) => onImageClick?.(src)}
            />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-[120ms] ease-out">
              <span className="bg-black/50 text-white p-2 rounded-full">
                <ZoomIn size={20} aria-hidden="true" />
              </span>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={openDrawer}
          data-tour={tourId}
          className={`w-full text-start min-h-[44px] p-3 flex gap-3 cursor-pointer
            transition-opacity duration-[120ms] ease-out hover:opacity-90
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest-ondark
            active:scale-[0.98]`}
          aria-label={he
            ? `פתח פירוט תזונתי: ${label}`
            : `Open nutrition breakdown: ${label}`}
        >
          {!hasPhoto && (
            <div className="w-11 h-11 rounded-md flex-shrink-0 bg-moss dark:bg-moss-dark flex items-center justify-center">
              <Apple size={20} className="text-forest-ondark/50" aria-hidden="true" />
            </div>
          )}
          <CardMeta
            label={label}
            timeLabel={timeLabel}
            calories={calories}
            planScore={planScore}
            healthScore={healthScore}
            language={language}
            he={he}
          />
        </button>
      </article>
    </div>
  );
}
